import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  MessageCircle,
  Calendar,
  BarChart3,
  Calculator,
  Settings,
  Plus,
  FolderKanban,
  LogOut,
  Sun,
  Moon,
  HelpCircle,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { leadsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { getInitials, statutLabel } from "@/lib/crmHelpers";

export default function CommandPalette({ onAddLead }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [leads, setLeads] = useState([]);
  const navigate = useNavigate();
  const settings = useSettings() || {};
  // Defensive: useContext can return non-array projects in pathological cases
  // (e.g. axios response wrapped weirdly, transient error). Always coerce.
  const activeProjectId = settings.activeProjectId;
  const projects = Array.isArray(settings.projects) ? settings.projects : [];
  const setActiveProject = settings.setActiveProject;
  const { logout } = useAuth();
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (leads.length) return;
    leadsApi.list(activeProjectId).then(setLeads).catch(() => setLeads([]));
  }, [open, activeProjectId, leads.length]);

  const filteredLeads = useMemo(() => {
    if (!query) return leads.slice(0, 6);
    const q = query.toLowerCase();
    return leads
      .filter((l) => `${l.nom} ${l.telephone} ${l.email || ""}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [leads, query]);

  const run = (fn) => {
    setOpen(false);
    setTimeout(fn, 50);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-[640px]">
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5 [&_[cmdk-item]_svg]:h-4 [&_[cmdk-item]_svg]:w-4"
        >
          <CommandInput
            placeholder="Rechercher un lead, naviguer, exécuter…"
            value={query}
            onValueChange={setQuery}
            data-testid="cmdk-input"
          />
          <CommandList className="max-h-[420px]">
            <CommandEmpty>Aucun résultat.</CommandEmpty>

            {filteredLeads.length > 0 && (
              <CommandGroup heading="Leads">
                {filteredLeads.map((l) => (
                  <CommandItem
                    key={l.id}
                    value={`lead-${l.id}`}
                    onSelect={() => run(() => navigate(`/leads/${l.id}`))}
                    data-testid={`cmdk-lead-${l.id}`}
                    className="gap-2"
                  >
                    <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                      {getInitials(l.nom)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{l.nom}</div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {l.telephone} · {statutLabel(l.statut)}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            <CommandSeparator />

            <CommandGroup heading="Actions">
              {onAddLead && (
                <CommandItem
                  value="action-add-lead"
                  onSelect={() => run(onAddLead)}
                  data-testid="cmdk-add-lead"
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Créer un nouveau lead
                </CommandItem>
              )}
              <CommandItem
                value="action-toggle-theme"
                onSelect={() => run(toggle)}
                data-testid="cmdk-theme"
                className="gap-2"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                Basculer le thème
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Navigation">
              <CommandItem value="nav-dashboard" onSelect={() => run(() => navigate("/"))} className="gap-2">
                <LayoutDashboard className="h-4 w-4" />
                Tableau de bord
              </CommandItem>
              <CommandItem value="nav-leads" onSelect={() => run(() => navigate("/leads"))} className="gap-2">
                <Users className="h-4 w-4" />
                Liste des leads
              </CommandItem>
              <CommandItem value="nav-messages" onSelect={() => run(() => navigate("/messages"))} className="gap-2">
                <MessageCircle className="h-4 w-4 text-emerald-600" />
                Messages WhatsApp
              </CommandItem>
              <CommandItem value="nav-calendar" onSelect={() => run(() => navigate("/calendrier"))} className="gap-2">
                <Calendar className="h-4 w-4" />
                Calendrier
              </CommandItem>
              <CommandItem value="nav-reports" onSelect={() => run(() => navigate("/rapports"))} className="gap-2">
                <BarChart3 className="h-4 w-4" />
                Rapports
              </CommandItem>
              <CommandItem value="nav-simulation" onSelect={() => run(() => navigate("/simulation"))} className="gap-2">
                <Calculator className="h-4 w-4 text-emerald-600" />
                Simulation
              </CommandItem>
              <CommandItem value="nav-settings" onSelect={() => run(() => navigate("/parametres"))} className="gap-2">
                <Settings className="h-4 w-4" />
                Paramètres
              </CommandItem>
              <CommandItem value="nav-help" onSelect={() => run(() => navigate("/aide"))} className="gap-2">
                <HelpCircle className="h-4 w-4" />
                Centre d'aide
              </CommandItem>
            </CommandGroup>

            {projects.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Projets">
                  <CommandItem
                    value="project-all"
                    onSelect={() => run(() => setActiveProject(null))}
                    className="gap-2"
                  >
                    <FolderKanban className="h-4 w-4" />
                    Tous les projets
                    {!activeProjectId && (
                      <span className="ml-auto text-[10px] font-bold text-emerald-600">ACTIF</span>
                    )}
                  </CommandItem>
                  {projects.map((p) => (
                    <CommandItem
                      key={p.id}
                      value={`project-${p.id}`}
                      onSelect={() => run(() => setActiveProject(p.id))}
                      className="gap-2"
                    >
                      <FolderKanban className="h-4 w-4 text-accent-gold" />
                      {p.nom}
                      {activeProjectId === p.id && (
                        <span className="ml-auto text-[10px] font-bold text-emerald-600">ACTIF</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            <CommandSeparator />
            <CommandGroup heading="Compte">
              <CommandItem
                value="logout"
                onSelect={() =>
                  run(async () => {
                    await logout();
                    navigate("/login");
                  })
                }
                className="gap-2 text-red-600"
              >
                <LogOut className="h-4 w-4" />
                Déconnexion
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
